/**
 * Depth sorting for the iso "world" layer (walls, multi-tile objects, characters).
 *
 * Why not a single scalar key (tx + ty)? A 1x3 bed and a character standing
 * beside its far end have overlapping key ranges: any scalar key gets one of
 * the two cases wrong. We instead sort by the classic box-separation rule:
 *
 *   Every world item has an axis-aligned box in tile space (footprint x/y
 *   extents and a z extent). For two items whose SCREEN rectangles overlap,
 *   A must be drawn before B if A is entirely on the far side of B along
 *   some axis: A.maxX <= B.minX, or A.maxY <= B.minY (further from the
 *   camera), or A.maxZ <= B.minZ (below it).
 *
 * Items whose screen rects do not overlap need no relation. The relations
 * form a DAG for floor-standing boxes with disjoint footprints (a fixed-
 * direction blocking relation between disjoint convex footprints is acyclic),
 * so a topological sort yields a correct order. Ambiguous pairs (footprints
 * that intersect, e.g. two characters on the same spot) get no edge and fall
 * back to a stable scalar key; any cycle (pathological stacked boxes) is
 * broken deterministically by ignoring the back edge.
 *
 * Cost per frame: O(n log n) key sort + sweep-and-prune pair search on
 * screen x + O(V + E) DFS. All buffers are reused; no per-frame allocation
 * once capacity has grown.
 */
/** Tolerance for "touching" boxes (shared faces count as separated). */
const EPS = 1e-6;
/**
 * Pure box comparator. Returns -1 if `a` must be drawn before `b`, 1 if `b`
 * must be drawn before `a`, 0 if the boxes give no (or a contradictory) answer.
 * Only meaningful for items whose screen projections overlap.
 */
export function compareBoxes(a, b) {
    return compareRaw(a.minX, a.minY, a.minZ, a.maxX, a.maxY, a.maxZ, b.minX, b.minY, b.minZ, b.maxX, b.maxY, b.maxZ);
}
function compareRaw(aMinX, aMinY, aMinZ, aMaxX, aMaxY, aMaxZ, bMinX, bMinY, bMinZ, bMaxX, bMaxY, bMaxZ) {
    const aBehind = aMaxX <= bMinX + EPS || aMaxY <= bMinY + EPS || aMaxZ <= bMinZ + EPS;
    const bBehind = bMaxX <= aMinX + EPS || bMaxY <= aMinY + EPS || bMaxZ <= aMinZ + EPS;
    if (aBehind && !bBehind)
        return -1;
    if (bBehind && !aBehind)
        return 1;
    return 0;
}
/** Scalar fallback key: box centre depth along the view axis, with a small z bias. */
export function scalarDepthKey(b) {
    return (b.minX + b.maxX + b.minY + b.maxY) * 0.5 + (b.minZ + b.maxZ) * 0.01;
}
export class DepthSorter {
    cap = 0;
    count = 0;
    // Struct-of-arrays item storage.
    minX = new Float64Array(0);
    minY = new Float64Array(0);
    minZ = new Float64Array(0);
    maxX = new Float64Array(0);
    maxY = new Float64Array(0);
    maxZ = new Float64Array(0);
    left = new Float64Array(0);
    top = new Float64Array(0);
    right = new Float64Array(0);
    bottom = new Float64Array(0);
    key = new Float64Array(0);
    tie = new Float64Array(0);
    rank = new Int32Array(0);
    byKey = new Int32Array(0);
    byLeft = new Int32Array(0);
    state = new Uint8Array(0);
    adjStart = new Int32Array(0);
    adjFill = new Int32Array(0);
    stackNode = new Int32Array(0);
    stackCursor = new Int32Array(0);
    /** Output: item indices (in add order) from back to front. */
    order = new Int32Array(0);
    // Edge list (behind -> front), grown on demand.
    edgeCap = 0;
    edgeBehind = new Int32Array(0);
    edgeFront = new Int32Array(0);
    adj = new Int32Array(0);
    edgeCount = 0;
    /** Pairs tested in the last sort (for stats). */
    pairTests = 0;
    byKeyCmp = (i, j) => {
        const d = this.key[i] - this.key[j];
        return d !== 0 ? d : this.tie[i] - this.tie[j];
    };
    byLeftCmp = (i, j) => this.left[i] - this.left[j];
    /** Starts a new frame. */
    begin() {
        this.count = 0;
    }
    /**
     * Adds one item; returns its index. `tie` must be unique and stable per
     * item (e.g. entity id) so equal keys never swap between frames (no flicker).
     */
    add(box, left, top, right, bottom, tie) {
        if (this.count === this.cap)
            this.grow(Math.max(64, this.cap * 2));
        const i = this.count++;
        this.minX[i] = box.minX;
        this.minY[i] = box.minY;
        this.minZ[i] = box.minZ;
        this.maxX[i] = box.maxX;
        this.maxY[i] = box.maxY;
        this.maxZ[i] = box.maxZ;
        this.left[i] = left;
        this.top[i] = top;
        this.right[i] = right;
        this.bottom[i] = bottom;
        this.key[i] = scalarDepthKey(box);
        this.tie[i] = tie;
        return i;
    }
    /** Sorts the items added since begin(); result in `order[0..count)`. */
    sort() {
        const n = this.count;
        this.edgeCount = 0;
        this.pairTests = 0;
        if (n === 0)
            return this.order;
        // 1. Scalar key order (stable via tie). Used for DFS root order and adjacency order.
        const byKey = this.byKey.subarray(0, n);
        for (let i = 0; i < n; i++)
            byKey[i] = i;
        byKey.sort(this.byKeyCmp);
        for (let r = 0; r < n; r++)
            this.rank[byKey[r]] = r;
        // 2. Sweep-and-prune on screen x to find overlapping pairs; add an edge per decided pair.
        const byLeft = this.byLeft.subarray(0, n);
        for (let i = 0; i < n; i++)
            byLeft[i] = i;
        byLeft.sort(this.byLeftCmp);
        const { left, right, top, bottom } = this;
        for (let a = 0; a < n; a++) {
            const i = byLeft[a];
            const ri = right[i];
            const ti = top[i];
            const bi = bottom[i];
            for (let b = a + 1; b < n; b++) {
                const j = byLeft[b];
                if (left[j] >= ri)
                    break;
                if (top[j] >= bi || ti >= bottom[j])
                    continue;
                this.pairTests++;
                const c = compareRaw(this.minX[i], this.minY[i], this.minZ[i], this.maxX[i], this.maxY[i], this.maxZ[i], this.minX[j], this.minY[j], this.minZ[j], this.maxX[j], this.maxY[j], this.maxZ[j]);
                if (c < 0)
                    this.addEdge(i, j);
                else if (c > 0)
                    this.addEdge(j, i);
            }
        }
        // 3. CSR adjacency: for each item, the items that must be drawn before it.
        const adjStart = this.adjStart;
        adjStart.fill(0, 0, n + 1);
        const E = this.edgeCount;
        for (let e = 0; e < E; e++)
            adjStart[this.edgeFront[e] + 1]++;
        for (let i = 0; i < n; i++)
            adjStart[i + 1] += adjStart[i];
        if (this.adj.length < E)
            this.adj = new Int32Array(Math.max(E, this.adj.length * 2));
        const adj = this.adj;
        const fill = this.adjFill;
        for (let i = 0; i < n; i++)
            fill[i] = adjStart[i];
        for (let e = 0; e < E; e++)
            adj[fill[this.edgeFront[e]]++] = this.edgeBehind[e];
        // Order each adjacency list by scalar rank so unconstrained neighbours keep key order (no jitter).
        const rank = this.rank;
        for (let i = 0; i < n; i++) {
            const s = adjStart[i];
            const end = adjStart[i + 1];
            for (let p = s + 1; p < end; p++) {
                const v = adj[p];
                const rv = rank[v];
                let q = p - 1;
                while (q >= s && rank[adj[q]] > rv) {
                    adj[q + 1] = adj[q];
                    q--;
                }
                adj[q + 1] = v;
            }
        }
        // 4. Iterative DFS topological sort, roots in key order. state: 0 new, 1 on stack, 2 done.
        const state = this.state;
        state.fill(0, 0, n);
        const order = this.order;
        const stackNode = this.stackNode;
        const stackCursor = this.stackCursor;
        let out = 0;
        for (let r = 0; r < n; r++) {
            const root = byKey[r];
            if (state[root] !== 0)
                continue;
            let sp = 0;
            stackNode[0] = root;
            stackCursor[0] = adjStart[root];
            state[root] = 1;
            while (sp >= 0) {
                const node = stackNode[sp];
                const cursor = stackCursor[sp];
                if (cursor < adjStart[node + 1]) {
                    stackCursor[sp] = cursor + 1;
                    const next = adj[cursor];
                    if (state[next] === 0) {
                        state[next] = 1;
                        sp++;
                        stackNode[sp] = next;
                        stackCursor[sp] = adjStart[next];
                    }
                    // state 1 => cycle: ignore this back edge. state 2 => already emitted.
                }
                else {
                    state[node] = 2;
                    order[out++] = node;
                    sp--;
                }
            }
        }
        return order;
    }
    addEdge(behind, front) {
        if (this.edgeCount === this.edgeCap) {
            const cap = Math.max(256, this.edgeCap * 2);
            const b = new Int32Array(cap);
            const f = new Int32Array(cap);
            b.set(this.edgeBehind);
            f.set(this.edgeFront);
            this.edgeBehind = b;
            this.edgeFront = f;
            this.edgeCap = cap;
        }
        this.edgeBehind[this.edgeCount] = behind;
        this.edgeFront[this.edgeCount] = front;
        this.edgeCount++;
    }
    grow(cap) {
        const f64 = (old) => {
            const a = new Float64Array(cap);
            a.set(old);
            return a;
        };
        this.minX = f64(this.minX);
        this.minY = f64(this.minY);
        this.minZ = f64(this.minZ);
        this.maxX = f64(this.maxX);
        this.maxY = f64(this.maxY);
        this.maxZ = f64(this.maxZ);
        this.left = f64(this.left);
        this.top = f64(this.top);
        this.right = f64(this.right);
        this.bottom = f64(this.bottom);
        this.key = f64(this.key);
        this.tie = f64(this.tie);
        this.rank = new Int32Array(cap);
        this.byKey = new Int32Array(cap);
        this.byLeft = new Int32Array(cap);
        this.state = new Uint8Array(cap);
        this.adjStart = new Int32Array(cap + 1);
        this.adjFill = new Int32Array(cap);
        this.stackNode = new Int32Array(cap);
        this.stackCursor = new Int32Array(cap);
        this.order = new Int32Array(cap);
        this.cap = cap;
    }
}
